import { useEffect, useRef, useState } from "react";
import { Joyride, STATUS } from "react-joyride";
import type { EventData, Step } from "react-joyride";
import { useAuth } from "@/auth/useAuth";
import { useOnboarding, type OnboardingKey } from "./useOnboarding";

interface Props {
  steps: Step[];
  flagKey: OnboardingKey;
  /**
   * Versione del contenuto di questo tour. Alzarla lo fa ripartire una volta
   * sola, anche per chi l'aveva già chiuso: è il modo di far vedere le funzioni
   * nuove a chi è già dentro il portale.
   */
  version?: number;
  /** Parent increments this to force replay. Each change starts a new tour. */
  replayNonce?: number;
  onClose?: () => void;
}

/**
 * Un passo si mostra solo se il suo elemento è in pagina **e** visibile: è la
 * regola unica per tutto ciò che c'è solo a volte — le voci di menù (sotto 768px
 * il menù non è nel DOM finché non si apre), il selettore elenco/schede (nascosto
 * sotto 640px), il cestino delle bozze, i moduli, la striscia delle utenze ferme.
 * Un passo puntato al vuoto mostrerebbe un riquadro su niente.
 */
function targetVisibile(step: Step): boolean {
  const target = step.target;
  if (typeof target !== "string" || target === "body") return true;
  let el: Element | null = null;
  try {
    el = document.querySelector(target);
  } catch {
    return false; // selettore malformato: meglio saltare che rompere il tour
  }
  if (!el) return false;
  const rect = el.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
}

export function OnboardingTour({ steps, flagKey, version = 1, replayNonce = 0, onClose }: Props) {
  const { hasRole, user } = useAuth();
  const isStaff = hasRole("affiliato") || hasRole("operatore");
  const { seenVersion, markSeen } = useOnboarding(flagKey);
  const seen = seenVersion >= version;

  const [running, setRunning] = useState(false);
  // Passi filtrati al momento dell'avvio: la pagina è già renderizzata, quindi
  // qui si sa davvero cosa c'è sullo schermo.
  const [stepsInCorso, setStepsInCorso] = useState<Step[]>([]);
  const isReplayRef = useRef(false);
  const lastNonceRef = useRef(0);
  const autoStartedRef = useRef(false);

  // Auto-start on first mount if staff and not seen. Once autostarted, never
  // ri-autostartare per lo stesso mount — markSeen è async e il `seen=false`
  // persiste per qualche frame dopo il finish, altrimenti il tour ripartirebbe
  // in loop fino al round-trip API.
  useEffect(() => {
    if (!seen && isStaff && user && !running && !autoStartedRef.current) {
      autoStartedRef.current = true;
      isReplayRef.current = false;
      avvia();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seen, isStaff, user]);

  // Start on replay nonce change
  useEffect(() => {
    if (replayNonce > 0 && replayNonce !== lastNonceRef.current) {
      lastNonceRef.current = replayNonce;
      isReplayRef.current = true;
      avvia();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [replayNonce]);

  function avvia() {
    const disponibili = steps.filter(targetVisibile);
    if (disponibili.length === 0) return;
    setStepsInCorso(disponibili);
    setRunning(true);
  }

  // Toggle body class while running
  useEffect(() => {
    if (running) {
      document.body.classList.add("tour-active");
      return () => {
        document.body.classList.remove("tour-active");
      };
    }
  }, [running]);

  if (!isStaff || !user) return null;

  function handleEvent(data: EventData) {
    const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED];
    if (finishedStatuses.includes(data.status)) {
      setRunning(false);
      if (!isReplayRef.current) {
        markSeen(version);
      }
      isReplayRef.current = false;
      document.body.classList.remove("tour-active");
      onClose?.();
    }
  }

  return (
    <Joyride
      key={`tour-${flagKey}-${lastNonceRef.current}-${running ? "on" : "off"}`}
      steps={stepsInCorso}
      run={running}
      continuous
      onEvent={handleEvent}
      locale={{
        back: "Indietro",
        close: "Chiudi",
        last: "Fine",
        next: "Avanti",
        nextWithProgress: "Avanti ({current} di {total})",
        open: "Apri guida",
        skip: "Salta",
      }}
      options={{
        showProgress: true,
        overlayClickAction: false,
        buttons: ["back", "skip", "primary"],
        primaryColor: "#4B9B2B",
        textColor: "#111827",
        overlayColor: "rgba(0, 0, 0, 0.55)",
        zIndex: 10000,
      }}
    />
  );
}
